import { getActiveLocale } from "../i18n";
import { THEME_STORAGE_KEY } from "../providers/ThemeProvider";
import { createLogger } from "../utils/logger";

const logger = createLogger("apiClient");

const DEFAULT_API_HOST =
  typeof window !== "undefined" && window.location.hostname
    ? window.location.hostname
    : "localhost";
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  `http://${DEFAULT_API_HOST}:8000/api/v1`;

type ClientPlatform = "web" | "mobile" | "desktop";

function detectPlatform(): ClientPlatform {
  if (typeof window !== "undefined") {
    // Tauri exposes __TAURI__ global
    if ((window as unknown as { __TAURI__?: unknown }).__TAURI__) {
      return "desktop";
    }
    // Capacitor exposes a global Capacitor object
    if ((window as unknown as { Capacitor?: unknown }).Capacitor) {
      return "mobile";
    }
  }
  return "web";
}

const CLIENT_PLATFORM: ClientPlatform =
  (import.meta.env.VITE_CLIENT_PLATFORM as ClientPlatform | undefined) ??
  detectPlatform();
const CLIENT_APP_ID =
  import.meta.env.VITE_CLIENT_APP_ID ??
  (CLIENT_PLATFORM === "desktop"
    ? "lotlocator-desktop"
    : CLIENT_PLATFORM === "mobile"
      ? "lotlocator-mobile"
      : "lotlocator-web");
const DEFAULT_DEVICE_LABEL =
  typeof navigator !== "undefined"
    ? navigator.platform || "web-browser"
    : "web-browser";

function detectTheme(): "light" | "dark" | undefined {
  if (typeof document !== "undefined") {
    const datasetTheme = document.documentElement.dataset.theme;
    if (datasetTheme === "light" || datasetTheme === "dark") {
      return datasetTheme;
    }
  }

  if (typeof window !== "undefined") {
    const stored = window.sessionStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") {
      return stored;
    }

    if (typeof window.matchMedia === "function") {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
      }
      return "light";
    }
  }

  return undefined;
}

export const AUTH_EXPIRED_EVENT = "lotlocator:auth-expired";

export interface ValidationErrorDetail {
  field: string;
  message: string;
  code?: string;
}

export interface ApiErrorBody {
  error?: string;
  message?: string;
  details?: {
    field?: string;
    fields?: ValidationErrorDetail[];
    [key: string]: unknown;
  };
}

export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export type RequestResponseType = "json" | "blob" | "text";

export interface RequestOptions extends RequestInit {
  responseType?: RequestResponseType;
}

export async function request<T>(
  path: string,
  init: RequestOptions = {}
): Promise<T> {
  const { responseType = "json", ...fetchInit } = init;
  const isFormData =
    typeof FormData !== "undefined" && fetchInit.body instanceof FormData;

  const headers = new Headers(fetchInit.headers ?? {});
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept-Language")) {
    headers.set("Accept-Language", getActiveLocale());
  }
  const theme = detectTheme();
  if (theme && !headers.has("X-UI-Theme")) {
    headers.set("X-UI-Theme", theme);
  }
  if (!headers.has("X-Client-Platform")) {
    headers.set("X-Client-Platform", CLIENT_PLATFORM);
  }
  if (CLIENT_APP_ID && !headers.has("X-Client-App-ID")) {
    headers.set("X-Client-App-ID", CLIENT_APP_ID);
  }
  if (!headers.has("X-Device-Label")) {
    headers.set("X-Device-Label", DEFAULT_DEVICE_LABEL);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      credentials: "include",
      ...fetchInit,
      headers
    });
  } catch (error) {
    const message =
      "Unable to reach the LotLocator API. Ensure the backend container is running and try again.";
    logger.error("API request failed before reaching server:", error);
    throw new ApiError(0, message, {
      error: "network_error",
      message
    });
  }

  const contentType = response.headers.get("content-type") ?? "";
  const isJSON = contentType.includes("application/json");
  const parsed = isJSON ? await response.clone().json().catch(() => undefined) : undefined;

  if (!response.ok) {
    const body = parsed as ApiErrorBody | undefined;
    if (response.status === 401) {
      const message =
        body?.message || body?.error || "Session expired. Please sign in again.";
      window.dispatchEvent(
        new CustomEvent<{ message: string }>(AUTH_EXPIRED_EVENT, {
          detail: { message }
        })
      );
    }

    const message =
      body?.message || body?.error || `Request failed with ${response.status}`;
    throw new ApiError(response.status, message, body);
  }

  if (responseType === "blob") {
    return (await response.blob()) as T;
  }

  if (responseType === "text") {
    return (await response.text()) as T;
  }

  return (parsed as T) ?? (undefined as T);
}

export async function authorizedRequest<T>(
  accessToken: string,
  path: string,
  init: RequestOptions = {}
): Promise<T> {
  const { headers, ...rest } = init;
  return request<T>(path, {
    ...rest,
    headers: {
      ...(headers ?? {}),
      // eslint-disable-next-line no-restricted-syntax
      Authorization: `Bearer ${accessToken}`
    }
  });
}

export { API_BASE_URL };

export function getValidationErrors(error: ApiError): Record<string, string> {
  const details = error.body?.details;
  if (!details) {
    return {};
  }

  const fieldErrors: Record<string, string> = {};

  if (Array.isArray(details.fields)) {
    for (const item of details.fields) {
      if (
        item &&
        typeof item.field === "string" &&
        typeof item.message === "string"
      ) {
        fieldErrors[item.field] = item.message;
      }
    }
  }

  if (
    Object.keys(fieldErrors).length === 0 &&
    typeof details.field === "string" &&
    typeof error.body?.message === "string"
  ) {
    fieldErrors[details.field] = error.body.message;
  }

  return fieldErrors;
}
